import { SettingsModal } from "@/components/SettingsModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FpDropdownMenu,
  FpDropdownMenuContent,
  FpDropdownMenuGroup,
  FpDropdownMenuItem,
  FpDropdownMenuLabel,
  FpDropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FREE_IN_BETA } from "@/constants";
import { api } from "@/lib/api/api";
import { cn, isMac } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import {
  ArrowUp,
  BookOpen,
  ExternalLink,
  Keyboard,
  LogOut,
  MessageSquare,
  PanelLeftClose,
} from "lucide-react";
import { Suspense, lazy, useCallback, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { Avatar } from "./Avatar";
import { FreeInBetaTag } from "./FreeInBetaTag";
import styles from "./UserMenu.module.css";

// Lazy load the FeedbackModal component
const FeedbackModal = lazy(() =>
  import("@/components/FeedbackModal").then(({ FeedbackModal }) => ({
    default: FeedbackModal,
  })),
);

type Shortcut = {
  name: string;
  keys: string;
  description: string;
  icon?: React.ReactNode;
};

const shortcuts: Shortcut[] = [
  {
    name: "Toggle Sidebar",
    keys: isMac ? "⌘B" : "Ctrl+B",
    description: "Show or hide the sidebar",
    icon: <PanelLeftClose className="h-4 w-4" />,
  },
  {
    name: "Keyboard Shortcuts",
    keys: isMac ? "⌘K" : "Ctrl+K",
    description: "Show keyboard shortcuts",
    icon: <Keyboard className="h-4 w-4" />,
  },
  {
    name: "Logout",
    keys: isMac ? "⇧⌘L" : "Shift+Ctrl+L",
    description: "Log out of your account",
    icon: <LogOut className="h-4 w-4" />,
  },
];

export function UserMenu() {
  const router = useRouter();
  const { user } = router.options.context.auth;
  const queryClient = useQueryClient();
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // TODO:
  // Hook this up to the actual credits
  const totalCredits = 100;
  const usedCredits = 0;

  const handleLogout = useCallback(async () => {
    await api.logout();
    router.options.context.auth.user = undefined;
    await queryClient.invalidateQueries();
    await queryClient.resetQueries();
    router.invalidate();
  }, [queryClient, router]);

  useHotkeys(
    "shift+meta+l",
    (e) => {
      e.preventDefault();
      setIsLogoutDialogOpen(true);
    },
    {
      enableOnFormTags: true,
      preventDefault: true,
      enabled: true,
    },
  );

  useHotkeys(
    "meta+k",
    (e) => {
      e.preventDefault();
      setShortcutsOpen((current) => !current);
    },
    {
      enableOnFormTags: true,
      preventDefault: true,
      enabled: true,
    },
  );

  // COMMENTED OUT: Hide settings page keyboard shortcut
  // useHotkeys(
  //   "meta+s,ctrl+s",
  //   (e) => {
  //     e.preventDefault();
  //     setSettingsOpen((open) => !open);
  //   },
  //   { enableOnFormTags: true, preventDefault: true, enabled: true },
  // );

  if (!user) {
    return null;
  }

  const userDisplayName = user.login || formatEmail(user.email);

  return (
    <>
      <FpDropdownMenu>
        <FpDropdownMenuTrigger asChild>
          <div className="p-2 m-2 hover:bg-muted cursor-pointer rounded">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7">
                  <Avatar sender="user" />
                </div>
                <span className="text-sm font-medium">{userDisplayName}</span>
              </div>
            </div>
          </div>
        </FpDropdownMenuTrigger>
        <FpDropdownMenuContent
          align="start"
          sideOffset={4}
          alignOffset={8}
          className={cn("w-56 p-0", styles.dropDownMenuContent)}
        >
          <FpDropdownMenuGroup className="p-1">
            <FpDropdownMenuLabel className="text-sm text-muted-foreground font-normal">
              {userDisplayName}
            </FpDropdownMenuLabel>
          </FpDropdownMenuGroup>
          <hr />

          <FpDropdownMenuGroup className="p-3">
            {FREE_IN_BETA && <FreeInBetaTag className="mb-3" />}

            <div
              className={cn(
                "text-xs",
                FREE_IN_BETA && "text-muted-foreground pointer-events-none",
              )}
            >
              <div className="bg-muted rounded p-3">
                <div className="flex gap-3 items-center">
                  <span className="flex-auto">Credits used</span>
                  <span>
                    {usedCredits}/{totalCredits}
                  </span>
                </div>
                <div
                  className={cn(
                    "rounded my-3 h-2 overflow-hidden",
                    styles.progressBarContainer,
                  )}
                >
                  <div
                    className="bg-primary w-4 h-2"
                    style={{
                      width: `${(usedCredits / totalCredits) * 100}%`,
                    }}
                  >
                    &nbsp;
                  </div>
                </div>
                <div className="flex gap-3 items-center">
                  <span
                    className={cn(
                      "flex-auto text-muted-foreground",
                      styles.creditsUsedLabel,
                    )}
                  >
                    {usedCredits} of your daily credits used
                  </span>
                  <span>Upgrade</span>
                </div>
              </div>
            </div>
          </FpDropdownMenuGroup>

          <hr />
          <FpDropdownMenuGroup className="p-1">
            {/* COMMENTED OUT: Hide settings menu item */}
            {/* <FpDropdownMenuItem
              className={cn("px-3", styles.dropDownMenuItem)}
              onClick={() => setSettingsOpen(true)}
            >
              <Settings className="mr-2 h-4 w-4" />
              <span className="flex-auto">Settings</span>
              <span className="text-muted-foreground">
                {isMac ? "⌘" : "ctrl"}S
              </span>
            </FpDropdownMenuItem> */}
            <FpDropdownMenuItem
              className={cn("px-3", styles.dropDownMenuItem)}
              onClick={() => setShortcutsOpen(true)}
            >
              <Keyboard className="mr-2 h-4 w-4" />
              <span className="flex-auto">Keyboard shortcuts</span>
              <span className="text-muted-foreground">
                {isMac ? "⌘" : "ctrl"}K
              </span>
            </FpDropdownMenuItem>
            <FpDropdownMenuItem className={cn("px-3", styles.dropDownMenuItem)}>
              <BookOpen className="mr-2 h-4 w-4" />
              <a
                href="https://docs.fiberplane.com/blog/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-auto flex items-center justify-between gap-1"
              >
                Blog
                <ExternalLink
                  className="h-4 w-4"
                  aria-label="Opens in new tab"
                />
              </a>
            </FpDropdownMenuItem>
            <FpDropdownMenuItem
              className={cn("px-3", styles.dropDownMenuItem)}
              onClick={() => setFeedbackOpen(true)}
            >
              <MessageSquare className="mr-2 h-4 w-4" />
              <span className="flex-auto">Leave feedback</span>
            </FpDropdownMenuItem>
          </FpDropdownMenuGroup>
          <hr />
          <FpDropdownMenuGroup className="p-1">
            <FpDropdownMenuItem
              className={cn("px-3", styles.dropDownMenuItem)}
              onSelect={(e) => {
                e.preventDefault();
                setIsLogoutDialogOpen(true);
              }}
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span className="flex-auto">Log out</span>
              <span className="text-muted-foreground flex items-center">
                <ArrowUp className="h-4 w-4" />
                {isMac ? "⌘" : "ctrl"}L
              </span>
            </FpDropdownMenuItem>
          </FpDropdownMenuGroup>
        </FpDropdownMenuContent>
      </FpDropdownMenu>

      <AlertDialog
        open={isLogoutDialogOpen}
        onOpenChange={setIsLogoutDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Are you sure you want to log out?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You'll need to log back in to continue building.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleLogout}>
              Log out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={shortcutsOpen} onOpenChange={setShortcutsOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Keyboard Shortcuts</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Action</TableHead>
                  <TableHead className="w-[100px]">Shortcut</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shortcuts.map((shortcut) => (
                  <TableRow key={shortcut.name}>
                    <TableCell className="font-medium flex items-center gap-2">
                      {shortcut.icon}
                      <span className="min-w-max">{shortcut.name}</span>
                    </TableCell>
                    <TableCell>
                      <kbd className="px-2 py-1 text-sm tracking-widest text-center rounded bg-muted inline-flex items-center">
                        {shortcut.keys}
                      </kbd>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <span className="min-w-max">{shortcut.description}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
      <SettingsModal />
      <Suspense fallback={null}>
        <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      </Suspense>
    </>
  );
}

// Format email to show first letter, three asterisks, and domain
function formatEmail(email: string) {
  const [localPart, domain] = email.split("@");
  return `${localPart.charAt(0)}***@${domain}`;
}
